import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as reportsApi from '@/api/reports.api';
import type { ReportType } from '@/api/reports.api';

/**
 * Query keys for reports
 */
export const reportKeys = {
  all: ['reports'] as const,
  overview: (days: number) => [...reportKeys.all, 'overview', days] as const,
  developer: (userId: string, days: number) => [...reportKeys.all, 'developer', userId, days] as const,
  team: (days: number) => [...reportKeys.all, 'team', days] as const,
  generated: (params?: Record<string, unknown>) => [...reportKeys.all, 'generated', params] as const,
  generatedOne: (id: string) => [...reportKeys.all, 'generated', id] as const,
  generatedHtml: (id: string) => [...reportKeys.all, 'generated', id, 'html'] as const,
};

/**
 * Fetch overview report
 */
export function useOverviewReport(days = 30) {
  return useQuery({
    queryKey: reportKeys.overview(days),
    queryFn: () => reportsApi.getOverviewReport(days),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch developer report
 */
export function useDeveloperReport(userId: string, days = 30) {
  return useQuery({
    queryKey: reportKeys.developer(userId, days),
    queryFn: () => reportsApi.getDeveloperReport(userId, days),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch team report
 */
export function useTeamReport(days = 30) {
  return useQuery({
    queryKey: reportKeys.team(days),
    queryFn: () => reportsApi.getTeamReport(days),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch generated reports
 */
export function useGeneratedReports(params?: {
  type?: ReportType;
  status?: string;
  startDate?: string;
  endDate?: string;
  projectId?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: reportKeys.generated(params),
    queryFn: () => reportsApi.getGeneratedReports(params),
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Fetch single generated report
 */
export function useGeneratedReport(reportId: string) {
  return useQuery({
    queryKey: reportKeys.generatedOne(reportId),
    queryFn: () => reportsApi.getGeneratedReport(reportId),
    enabled: !!reportId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Fetch generated report HTML
 */
export function useGeneratedReportHtml(reportId: string) {
  return useQuery({
    queryKey: reportKeys.generatedHtml(reportId),
    queryFn: () => reportsApi.getGeneratedReportHtml(reportId),
    enabled: !!reportId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Generate daily report mutation
 */
export function useGenerateDailyReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId?: string) => reportsApi.generateDailyReport(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
}

/**
 * Generate weekly report mutation
 */
export function useGenerateWeeklyReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId?: string) => reportsApi.generateWeeklyReport(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
}

/**
 * Send report email mutation
 */
export function useSendReportEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportId: string) => reportsApi.sendReportEmail(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
}
